/**
 * Formatação das mensagens de subscrição automática (underwriting) exibidas
 * quando a cotação cai em análise.
 *
 * A variação por código de mensagem é resolvida por configuração
 * (`MESSAGE_BUILDERS`): para dar tratamento amigável a um código novo, basta
 * adicionar uma entrada no registry — sem tocar nos componentes que exibem
 * as mensagens.
 */
import type { BusinessActivity } from '@/services/business-activities';
import { Fragment, type ReactNode } from 'react';

/** Shape mínimo comum às mensagens de UW dos produtos (business, vida, etc). */
export type UnderwritingMessageLike = {
  messageCode?: string;
  messageDetail?: string;
  ruleName?: string;
};

export type UnderwritingMessageContext = {
  /**
   * Atividades exercidas nos locais de risco da cotação
   * (`activityTypePerformed`) — usadas por códigos que citam a atividade.
   */
  activities?: string[];
  /**
   * `true` quando algum local de risco pediu desconto para a subscrição
   * (`underwritingDiscountRequestInd === 'Y'`) — acrescenta o motivo de
   * desconto manual à lista.
   */
  hasUnderwritingDiscountRequest?: boolean;
};

type MessageBuilder = (
  _message: UnderwritingMessageLike,
  _context: UnderwritingMessageContext
) => ReactNode | undefined;

/**
 * Resolve os códigos de atividade (`activityTypePerformed`) para as
 * descrições do catálogo de atividades empresariais. Enquanto o catálogo não
 * carregou, retorna lista vazia (as mensagens caem no formato padrão);
 * código sem correspondência no catálogo carregado mantém o próprio código.
 */
export function resolveBusinessActivityNames(
  activityCodes: string[],
  catalog: BusinessActivity[] | undefined
): string[] {
  if (!catalog?.length) return [];
  return activityCodes.map(activityCode => {
    const match = catalog.find(
      activity => String(activity.code) === activityCode.trim()
    );
    return match?.description || activityCode;
  });
}

export function capitalizeFirst(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/**
 * Registry de mensagens amigáveis por código. Retornar `undefined` faz a
 * mensagem cair no formato padrão `[código] detalhe`.
 */
const MESSAGE_BUILDERS: Record<string, MessageBuilder> = {
  'ALBA-PA-UW-B0003': (_message, { activities }) => {
    const uniqueActivities = [
      ...new Set((activities ?? []).map(activity => activity.trim())),
    ].filter(Boolean);

    if (uniqueActivities.length === 0) return undefined;

    const activityNames = uniqueActivities.map(capitalizeFirst);

    if (activityNames.length === 1) {
      return (
        <>
          Motivo: A atividade <strong>{activityNames[0]}</strong>, é uma
          atividade sob consulta
        </>
      );
    }
    return (
      <>
        Motivo: As atividades{' '}
        {activityNames.map((activityName, index) => (
          <Fragment key={activityName}>
            {index > 0 && ', '}
            <strong>{activityName}</strong>
          </Fragment>
        ))}
        , são atividades sob consulta
      </>
    );
  },
};

/**
 * Converte a lista de mensagens de UW em conteúdos prontos para exibição.
 * Códigos com builder no registry ganham mensagem amigável; os demais exibem
 * apenas o detalhe retornado pelo backend.
 */
export function formatUnderwritingMessages(
  messages: UnderwritingMessageLike[],
  context: UnderwritingMessageContext = {}
): ReactNode[] {
  const formatted: ReactNode[] = messages
    .map(message => {
      const code = message.messageCode?.trim();

      const customMessage = code
        ? MESSAGE_BUILDERS[code]?.(message, context)
        : undefined;
      if (customMessage) return customMessage;

      const detail =
        message.messageDetail?.trim() || message.ruleName?.trim() || '';
      return detail;
    })
    .filter(Boolean);

  if (context.hasUnderwritingDiscountRequest) {
    formatted.push('Motivo: solicitação de desconto manual pela subscrição');
  }

  return formatted;
}
